import { Box, Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { BaseUser, SchoolProperty, StudentRegistration } from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { SchoolUserRole } from '@schooly/constants';
import { LockIcon, TagSelect } from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import React, { FC, PropsWithChildren, useMemo } from 'react';
import { useIntl } from 'react-intl';

import useSchoolYears from '../../../hooks/useSchoolYears';
import { DisabledRegistrationStatus, RegistrationForm } from './StudentRegistrationForm';
import { CanceledEnrollmentParams } from './useReEnrollmentActions';
import { getNextYearEnrollments, isAcceptedEnrollment, isRejectedEnrollment } from './utils';
type UseReEnrollmentStatusesProps = {
  enrollmentFormStatuses: RegistrationForm['statuses'];
  canceledEnrollmentParams?: CanceledEnrollmentParams;
  statuses: SchoolProperty[];
  enrollment?: StudentRegistration;
  enrollments: StudentRegistration[];
};

export const useReEnrollmentStatuses = ({
  enrollmentFormStatuses,
  canceledEnrollmentParams,
  statuses,
  enrollment,
  enrollments,
}: UseReEnrollmentStatusesProps): DisabledRegistrationStatus[] => {
  const { $t } = useIntl();
  const { schoolYears } = useSchoolYears();

  return useMemo(() => {
    if (!enrollment) return [];

    const tag = (
      <ReEnrollmentTag>{$t({ id: 'students-ReEnrollment-NotReEnrolled' })}</ReEnrollmentTag>
    );

    if (canceledEnrollmentParams) {
      const { id, statusId, user } = canceledEnrollmentParams;
      const statusName = statuses.find((s) => s.id === statusId)?.name?.toLowerCase() ?? '';

      const lockIconLabel = $t({ id: 'students-ReEnrollment-CannotEditResponse' });

      return [
        {
          formId: id,
          disabledFields: ['school_property_id'],
          tag,
          endAdornment: (
            <StatusEndAdornment
              title={$t(
                { id: 'students-ReEnrollment-MarkedAs' },
                {
                  statusName,
                  userName: getUserFullName(user),
                },
              )}
              user={user}
            />
          ),
          required: true,
          lockIcon: (
            <Tooltip title={lockIconLabel}>
              <IconButton inverse>
                <LockIcon />
              </IconButton>
            </Tooltip>
          ),
        },
      ];
    }

    const nextYearEnrollments = getNextYearEnrollments({
      schoolYears,
      enrollmentYearId: enrollment.school_year.id,
      enrollments,
    });

    const isEnrollmentAccepted = isAcceptedEnrollment(enrollment);
    const isEnrollmentRejected = isRejectedEnrollment(enrollment);

    const currentEnrollmentStatus = enrollment.enrollment_status;

    const acceptedEnrollmentStatus = isEnrollmentAccepted ? currentEnrollmentStatus : undefined;
    const rejectedEnrollmentStatus = isEnrollmentRejected
      ? currentEnrollmentStatus
      : nextYearEnrollments.find(isRejectedEnrollment)?.enrollment_status;

    if (!acceptedEnrollmentStatus && !rejectedEnrollmentStatus) return [];

    return enrollmentFormStatuses.reduce<DisabledRegistrationStatus[]>(
      (acc, { school_property_id, formId }) => {
        if (!formId) return acc;
        const isReadOnly = enrollment.statuses.some((s) => s.id === formId && !!s.read_only);

        if (!isReadOnly) return acc;

        const status = statuses.find((s) => s.id === school_property_id);
        const isCurrentStatus = !!status?.category?.current;
        const isFinalStatus = !!status?.category?.final;

        const isCurrentOrFinalStatus = acceptedEnrollmentStatus ? isCurrentStatus : isFinalStatus;

        const createdBy = isCurrentStatus
          ? acceptedEnrollmentStatus?.created_by
          : rejectedEnrollmentStatus?.created_by;

        const createdByParent = createdBy?.role === SchoolUserRole.Parent;

        const showEndAdornment = isCurrentOrFinalStatus && createdBy && !createdByParent;

        const lockIconLabel =
          rejectedEnrollmentStatus && createdByParent
            ? $t({ id: 'students-ReEnrollment-CannotEditParentResponse' })
            : $t({ id: 'students-ReEnrollment-CannotEditCreatedResponse' });

        const allStatusFields: DisabledRegistrationStatus['disabledFields'] = [
          'school_property_id',
          'applies_from',
        ];

        const disabledFieldsAfterReject: DisabledRegistrationStatus['disabledFields'] =
          createdByParent ? allStatusFields : ['school_property_id'];

        return [
          ...acc,
          {
            formId,
            disabledFields: rejectedEnrollmentStatus ? disabledFieldsAfterReject : allStatusFields,
            endAdornment: showEndAdornment ? (
              <StatusEndAdornment
                title={$t(
                  {
                    id: 'students-ReEnrollment-ConvertedBy',
                  },
                  { userName: getUserFullName(createdBy) },
                )}
                user={createdBy}
              />
            ) : (
              <Box position="absolute" right={2} top={12}>
                <Icon inverse>
                  <LockIcon />
                </Icon>
              </Box>
            ),
            lockIcon: (
              <Tooltip title={lockIconLabel}>
                <IconButton inverse>
                  <LockIcon />
                </IconButton>
              </Tooltip>
            ),
            tag: rejectedEnrollmentStatus ? tag : undefined,
          },
        ];
      },
      [],
    );
  }, [
    $t,
    canceledEnrollmentParams,
    enrollment,
    enrollmentFormStatuses,
    enrollments,
    schoolYears,
    statuses,
  ]);
};

type StatusEndAdornmentProps = {
  title: string;
  user: BaseUser;
};

const StatusEndAdornment: FC<StatusEndAdornmentProps> = ({ title, user }) => (
  <Tooltip
    title={
      <Stack gap={1}>
        <Typography>{title}</Typography>
      </Stack>
    }
    componentsProps={{
      tooltip: {
        sx: (theme) => ({
          padding: theme.spacing(1.25),
        }),
      },
    }}
  >
    <Box position="absolute" right={7} top={7.25}>
      <AvatarAuth user={user} />
    </Box>
  </Tooltip>
);

export const ReEnrollmentTag: FC<PropsWithChildren> = ({ children }) => (
  <TagSelect
    sx={{ backgroundColor: (theme) => theme.palette.background.paper }}
    label={
      <Typography variant="body1" color={(theme) => `${theme.palette.common.grey2} !important`}>
        {children}
      </Typography>
    }
  />
);
